import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  CardContent,
  Input
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { TestInboxField, useCan, useTestInbox } from '@/domains/auth';
import { accountsApi } from '../../api/accounts-api';
import type {
  IAdminParticipantAccount,
  IAdminParticipantDetail,
  IAdminParticipantInvitation
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import type { ICreateAccount } from '../../api/account-contracts';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import {
  AccountKindChip,
  ActivityFactItems,
  AddressFact,
  absent,
  ownAccounts,
  relationWords
} from '../../components/account-facts';
import { Fact, Section } from '../../components/admin-page';
import { ParticipantLink } from '../../components/participant-link';
import { UnverifiedBadge } from '../../components/unverified-badge';
import { userPath } from '../../paths';
import { refusalMessage, statusRefusal } from '../../refusal';
import { CREATE_ACCOUNT_FORBIDDEN, IDP_ADDRESS_TAKEN } from '../create-account-card';
import { AccountCredentials } from './credentials';
import { type IInvitationNote, InvitationsList, InvitationsNoLongerHeld } from './invitations';
import {
  type ISaidNote,
  NOT_REREAD,
  NoteLine,
  OUT_OF_DATE,
  sentence,
  unanswered,
  unansweredNote
} from './said';

/**
 * Account & access, once for the household (R7): the distinct accounts acting for any member,
 * each with its login state in `account-facts.tsx`'s words and the members it acts for, and the
 * link and its undo (block 6) — the only writes on the hub that change who can sign in as
 * somebody, so every refusal is told apart by status and `type` and every write re-reads the hub.
 * For a viewer holding `accounts.browse`, each account also carries the user card's Sign-in card
 * and its roles (R13, `credentials.tsx`), over a read of its own.
 */

/**
 * The accounts acting for the household, each drawn once — the block the participant card
 * repeated on every participant an account acted for, which is where one person read as two or
 * three people. An account is keyed by `accountId` across the members' cards, and what it acts
 * for is its own `participants` — **not** each a member of this household: survey-api groups the
 * household as what the opened participant's accounts act for, one level, so a second account of
 * a member (migrated data: a member some other login also acts for) can act for somebody further
 * out. That somebody is named and linked, and offered no Unlink here: whether this is their last
 * account is on their card, which this hub does not carry. Its login state is the
 * user card's part that staff need to answer "why can I not sign in", and no further: see the
 * page's header on the identity. A viewer holding `accounts.browse` gets the rest — how it signs
 * in, its invitations and roles — per account, from `AccountCredentials`' own read.
 *
 * **And which account acts for whom, since survey-api's block 6 (F3).** Each link can be undone,
 * member by member, and a participant with none can be given one — the one remedy the 206
 * migrated people whose legacy address went to another login have, since neither registration
 * nor a reset can reach them. Both writes are `accounts.manage` (SR-228), a permission of their
 * own beside the hub's `responses.browse`: without it the link panel and every Unlink are drawn
 * off, under one `NotPermitted` sentence at the section's top. What each write was told is said
 * at the top of the section and held by it, because the re-read that follows every write is the
 * read that takes an unlinked account away, button and all.
 *
 * **"Has an account" is the opened participant's `self` links** (SR-416's B2-1). A `contact` link
 * — an accepted invitation's point of contact — acts for the participant under its own login, and
 * survey-api's 409 `participant-has-account` does not count it: so the link panel is offered while
 * no `self` link acts for the opened participant, a contact drawn beside it as "Point of contact"
 * with its own Unlink, and the last-account warning is the last `self` link's. **An assistant is
 * offered on a person whatever links it has** (SR-487 ruling 19, PFR §8): survey-api links one
 * beside any `self`, and refuses one on a team or firm.
 *
 * **Under the accounts, each member's point-of-contact invitations** (SR-416, `invitations.tsx`),
 * whose Resend and Revoke share the section's `accounts.manage` and its `NotPermitted` sentence;
 * what each was told is held here per invitation id.
 */
export function AccountsSection({
  members,
  anchor,
  search,
  written,
  reread,
  responses,
  cards = members
}: {
  members: IAdminParticipantDetail[];
  anchor: string;
  search: string;
  written: number;
  reread: () => Promise<boolean>;
  /** The household's responses — an invitation names the response that wrote it. */
  responses: IOversightResponseRow[];
  /** Every card the hub carries, where they are more than `members` — a person's record page
   *  draws the person's accounts alone (SR-391), and a link to another participant on their login
   *  is still undone here, its last-account warning read off that participant's card. */
  cards?: IAdminParticipantDetail[];
}) {
  const [note, setNote] = React.useState<Nullable<IAccountsNote>>(null);
  const [invitationNotes, setInvitationNotes] = React.useState<Record<string, IInvitationNote>>(
    {}
  );
  const sayAboutInvitation = React.useCallback(
    (invitation: IAdminParticipantInvitation, said: Nullable<ISaidNote>) =>
      setInvitationNotes((held) => {
        const { [invitation.invitationId]: _dropped, ...rest } = held;
        return said
          ? { ...rest, [invitation.invitationId]: { email: invitation.email, note: said } }
          : rest;
      }),
    []
  );
  const invited = members.some((member) => (member.invitations ?? []).length > 0);
  const [opened] = members;
  const accounts = distinctAccounts(members);
  // The link is offered while no account is the opened participant's own: a point of contact
  // (`contact`) is not one, and survey-api's 409 counts the `self` links alone (SR-416, F1).
  const hasOwn = ownAccounts(opened.accounts).length > 0;
  const relations: LinkRelation[] = [
    ...(hasOwn ? [] : (['self'] as const)),
    ...(opened.kind === 'person' ? (['assistant'] as const) : [])
  ];
  const can = useCan();
  const canManage = can('accounts.manage');
  const canBrowseAccounts = can('accounts.browse');
  const reasonId = React.useId();
  const notPermitted = canManage ? undefined : reasonId;

  return (
    <Section title="Account & access" anchor={anchor}>
      <CardContent className="grid gap-3">
        {note && <NoteLine note={note} />}
        {notPermitted && (
          <NotPermitted
            id={notPermitted}
            what={
              invited
                ? 'linking or unlinking accounts, or resending or revoking invitations'
                : 'linking or unlinking accounts'
            }
          />
        )}
        {!hasOwn && (
          <>
            <p className="text-sm">
              {absent(
                accounts.length === 0
                  ? 'No account acts for this participant, so nobody can sign in as them.'
                  : 'No account of their own acts for this participant — only points of contact, ' +
                      'who sign in as themselves.'
              )}
            </p>
          </>
        )}
        {relations.length > 0 && (
          <LinkAccount
            // Drawn afresh when the links offered change: their own, once offered, is the choice.
            key={relations.join()}
            participantId={opened.participantId}
            displayName={opened.displayName}
            relations={relations}
            contacts={opened.accounts
              .filter((account) => account.relation === 'contact')
              .map((account) => account.email.toLowerCase())}
            notPermitted={notPermitted}
            say={setNote}
            reread={reread}
          />
        )}
        {accounts.length > 0 && (
          <ul className="grid gap-3">
            {accounts.map((account) => (
              <AccountItem
                key={account.accountId}
                account={account}
                members={members}
                cards={cards}
                search={search}
                canBrowseAccounts={canBrowseAccounts}
                written={written}
                notPermitted={notPermitted}
                say={setNote}
                reread={reread}
              />
            ))}
          </ul>
        )}
        {members.map((member) => (
          <InvitationsList
            key={member.participantId}
            member={member}
            multi={members.length > 1}
            responses={responses}
            notPermitted={notPermitted}
            notes={invitationNotes}
            say={sayAboutInvitation}
            reread={reread}
          />
        ))}
        <InvitationsNoLongerHeld
          notes={invitationNotes}
          held={
            new Set(
              members.flatMap((member) =>
                (member.invitations ?? []).map((invitation) => invitation.invitationId)
              )
            )
          }
        />
      </CardContent>
    </Section>
  );
}

/** Each account once, in the order the household's cards first name it — the opened
 *  participant's first. The scalars are the account's, the same on every card that carries it. */
function distinctAccounts(members: IAdminParticipantDetail[]): IAdminParticipantAccount[] {
  const seen = new Map<string, IAdminParticipantAccount>();
  for (const member of members) {
    for (const account of member.accounts) {
      if (!seen.has(account.accountId)) seen.set(account.accountId, account);
    }
  }
  return [...seen.values()];
}

/** What a link or an unlink was told. A refusal is loud; the rest are what happened. */
interface IAccountsNote {
  tone: 'done' | 'refused';
  title?: string;
  text: string;
}

function AccountItem({
  account,
  members,
  cards,
  search,
  canBrowseAccounts,
  written,
  notPermitted,
  say,
  reread
}: {
  account: IAdminParticipantAccount;
  members: IAdminParticipantDetail[];
  cards: IAdminParticipantDetail[];
  search: string;
  canBrowseAccounts: boolean;
  written: number;
  /** The id of the section's `NotPermitted` sentence; present, every Unlink is drawn off. */
  notPermitted?: string;
  say: (note: Nullable<IAccountsNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const openedId = members[0].participantId;
  return (
    <li className="grid gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-2">
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        {/* `accounts.browse`, the user card's own: offered without it, it would be a link
            `AdminRoute` bounces. `users-page.tsx` addresses it the same way. */}
        {canBrowseAccounts ? (
          <Link
            to={userPath(account.email)}
            className="font-medium break-all text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            {account.email}
          </Link>
        ) : (
          <span className="font-medium break-all">{account.email}</span>
        )}
        <AccountKindChip kind={account.kind} />
        <Badge variant={account.status === 'active' ? 'outline' : 'destructive'}>
          {account.status}
        </Badge>
      </div>

      <AddressFact emailVerifiedAt={account.emailVerifiedAt} />
      <ActivityFactItems activity={account.activity} />

      <div className="sm:col-span-2">
        <Fact
          label="Acts for"
          value={
            <ul aria-label={`${account.email} acts for`} className="grid gap-2">
              {account.participants.map((participant) => {
                // The link's own facts are on the member's card: its relation, and whether this
                // is the member's only account — whose unlink leaves nobody. A participant
                // outside the household has no card here, so neither is known, and the unlink is
                // left to their own page, where the last-account warning can be told true.
                const card = cards.find(
                  (member) => member.participantId === participant.participantId
                );
                const link = card?.accounts.find((held) => held.accountId === account.accountId);
                const relation = link ? relationWords(link.relation) : null;
                return (
                  <li
                    key={participant.participantId}
                    className="flex flex-wrap items-center gap-2"
                  >
                    {participant.participantId === openedId ? (
                      <>
                        <span className="font-medium">{participant.displayName}</span>
                        <Badge variant="outline">Opened</Badge>
                      </>
                    ) : (
                      <ParticipantLink participant={participant} search={search} />
                    )}
                    <Badge variant="soft">{participant.kind}</Badge>
                    {participant.unverified && <UnverifiedBadge />}
                    {/* Said only where the link is not the participant's own: "Point of
                        contact" for an accepted invitation's (SR-416). */}
                    {relation && <Badge variant="outline">{relation}</Badge>}
                    {card ? (
                      <UnlinkControl
                        account={account}
                        participantId={participant.participantId}
                        displayName={participant.displayName}
                        // The warning is the last `self` link's: a point of contact left
                        // behind is no way in for the participant.
                        last={link?.relation === 'self' && ownAccounts(card.accounts).length === 1}
                        notPermitted={notPermitted}
                        say={say}
                        reread={reread}
                      />
                    ) : (
                      <span className="basis-full text-muted-foreground">
                        Not in this household — the link is undone from their own page, which
                        knows whether it is their last account.
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          }
        />
      </div>

      {canBrowseAccounts ? (
        <div className="sm:col-span-2">
          <AccountCredentials email={account.email} written={written} reread={reread} />
        </div>
      ) : (
        <p className="text-muted-foreground sm:col-span-2">
          How the account signs in — its identity and its invitations — is on the user card,
          which your Team Portal roles do not open.
        </p>
      )}
    </li>
  );
}

/**
 * `DELETE …/account-links/{accountId}` (block 6, R5) — this account stops acting for this
 * participant.
 *
 * Confirmed first, and the confirmation says what it costs, because the cost lands on somebody
 * who is not in the room: the account loses the participant's responses and cannot start a
 * survey for them from its very next request — survey-api revokes no session, since access is
 * read per request — and, on the last link, nobody can sign in as the participant until a link
 * is made again. Any link can be undone, whoever made it: a wrong one shows a person's answers
 * to somebody else, and its repair cannot depend on who made it.
 *
 * A 404 is a link already gone — somebody else unlinked it, and the hub was out of date — so
 * the hub is read again and the note says so; nothing else about the refusal is guessed at.
 */
function UnlinkControl({
  account,
  participantId,
  displayName,
  last,
  notPermitted,
  say,
  reread
}: {
  account: IAdminParticipantAccount;
  participantId: string;
  displayName: string;
  last: boolean;
  /** The id of the section's `NotPermitted` sentence; present, the press is drawn off. */
  notPermitted?: string;
  say: (note: Nullable<IAccountsNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const unlink = async () => {
    setBusy(true);
    say(null);
    try {
      await participantsApi.unlinkAccount(participantId, account.accountId);
      const read = await reread();
      say({
        tone: 'done',
        text:
          `Unlinked ${account.email}. It no longer acts for ${displayName}.` +
          (read ? '' : NOT_REREAD)
      });
    } catch (unlinkError) {
      if (unanswered(unlinkError)) {
        say(unansweredNote(await reread(), 'link'));
      } else if (unlinkError instanceof ApiError && unlinkError.status === 404) {
        const read = await reread();
        say({
          tone: 'refused',
          title: 'Nothing to unlink',
          text:
            `${account.email} no longer acted for ${displayName} — the page was out of date. ` +
            (read ? 'It has been read again.' : OUT_OF_DATE)
        });
      } else {
        say({
          tone: 'refused',
          title: 'Not unlinked',
          text: refusalMessage(unlinkError, 'Could not unlink this account')
        });
      }
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
      <AlertDialogTrigger asChild>
        {/* Named for its account and its member: an account acting for two carries two, and a
            screen reader lists buttons by name. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || notPermitted !== undefined}
          aria-describedby={notPermitted}
          aria-label={`Unlink ${account.email} from ${displayName}`}
        >
          Unlink
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Unlink {account.email} from {displayName}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {account.email} will no longer act for {displayName}: from its next request it cannot
            open their responses or start a survey for them. Its sessions stay signed in, and
            nothing else about the account changes.
            {/* Inside the description, so the dialog announces the costliest sentence too. */}
            {last && (
              <span className="mt-2 block font-medium text-destructive">
                It is the only account acting for {displayName} — nobody will be able to sign in
                as them until an account is linked again from their page.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(event) => {
              // Held open until the answer is in, as the verification's confirmation is.
              event.preventDefault();
              void unlink();
            }}
          >
            {busy ? 'Unlinking…' : 'Unlink it'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

type LinkMode = 'existing' | 'new';

/** The relations a staff link writes. */
type LinkRelation = 'self' | 'assistant';

/**
 * A link's refusal in words, told apart by status and `type` alone — never by `title`, which is
 * survey-api's prose and free to change. 422 `invalid-action` naming `email` is the address
 * holding no account (staff learn that from seat creation's 409 already, so nothing new leaks);
 * 409 `participant-has-account` is an account acting for them since the hub was read; 404 is
 * a participant survey-api no longer holds. Anything else — a blank address's 400 among them —
 * is said in the server's own reason.
 */
function linkRefusal(
  error: unknown,
  address: string,
  displayName: string,
  contacts: readonly string[]
): string {
  if (error instanceof ApiError) {
    const aboutEmail = (error.problem?.problems ?? []).some(
      (problem) => problem.target === 'email'
    );
    if (error.status === 422 && error.isProblem(ProblemType.invalidAction) && aboutEmail) {
      return (
        `No account has the address ${address}. Check it — or choose “A new account” to ` +
        'create one there and link it.'
      );
    }
    if (error.status === 409 && error.isProblem(ProblemType.participantHasAccount)) {
      return `An account already acts for ${displayName} — the page was out of date.`;
    }
    // The account is linked here already (SR-416's B2-2): the link is one row per account and
    // participant, answered `already-linked` (SR-487), or `code-in-use` by survey-api's
    // constraint handler. An address already on the card is the usual cause — an assistant is
    // offered on a person whatever links it has — so it is said as a fact; as a point of contact
    // where the card shows that address as one.
    if (
      error.status === 409 &&
      (error.isProblem(ProblemType.codeInUse) || error.isProblem(ProblemType.alreadyLinked))
    ) {
      return contacts.includes(address.toLowerCase())
        ? `${address} is already linked to ${displayName} as a point of contact — unlink it ` +
            'first.'
        : `${address} is already linked to ${displayName}.`;
    }
    if (error.status === 404) {
      return `The system holds no participant under this id any more. ${OUT_OF_DATE}`;
    }
  }
  return refusalMessage(error, 'Could not link this account');
}

/**
 * Giving a participant with no account of its own its way in — `POST …/account-links { email }`
 * (block 6, R2–R4), which writes a `self` link, offered only while none acts for them: survey-api
 * refuses a second one, `participant-has-account`, counting `self` links alone (SR-416) — a point
 * of contact beside it is no refusal. The same account already linked as that point of contact is
 * 409 `code-in-use` (one link per account and participant), said as such.
 *
 * **Two remedies, and they reduce to one link.** The same person under another login: link the
 * account that holds their address. A different person, or a shared mailbox: create an advisor
 * seat at another address — the existing `POST /accounts`, which queues its owner's invitation
 * to sign in and draws the test inbox for it — then link that. Two calls from one form, as
 * survey-api chose over a composite route: a second call that fails leaves a seat with no
 * participant, which the link simply retries — so the form switches to the existing account,
 * address kept, and one press finishes it. A seat already at that address (409) is offered the
 * same way.
 *
 * The address is sent as typed — survey-api trims it and matches it in any case — and judged
 * nowhere here, like every address in this app.
 *
 * **Their own account, or an assistant** (SR-487 ruling 19): on a person the link is asked as
 * one of the two, and once the person has an account of its own as an assistant alone; on a team
 * or firm it is their own, asked as before. An assistant's link sends `relation: "assistant"`.
 */
function LinkAccount({
  participantId,
  displayName,
  relations,
  contacts,
  notPermitted,
  say,
  reread
}: {
  participantId: string;
  displayName: string;
  /** The links offered, in order — the first chosen to begin with. */
  relations: readonly LinkRelation[];
  /** The addresses of the participant's points of contact, lower-cased — what a 409
   *  `code-in-use` is said as. */
  contacts: readonly string[];
  /** The id of the section's `NotPermitted` sentence; present, the whole panel is drawn off. */
  notPermitted?: string;
  say: (note: Nullable<IAccountsNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const [mode, setMode] = React.useState<LinkMode>('existing');
  const [relation, setRelation] = React.useState<LinkRelation>(relations[0]);
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const inbox = useTestInbox();
  const modeName = React.useId();
  const address = email.trim();
  const off = notPermitted !== undefined;

  /** The link, both modes' last call. Resolves whether it is known to have landed; what it was
   *  told is said here, in the words the mode passes for each outcome. */
  const link = async (
    done: (linked: string) => string,
    refused: (error: unknown) => IAccountsNote,
    unknown: (read: boolean) => IAccountsNote
  ): Promise<boolean> => {
    try {
      const linked = await participantsApi.linkAccount(
        participantId,
        address,
        relation === 'assistant' ? 'assistant' : undefined
      );
      const read = await reread();
      say({ tone: 'done', text: done(linked.email) + (read ? '' : NOT_REREAD) });
      return true;
    } catch (linkError) {
      if (unanswered(linkError)) {
        say(unknown(await reread()));
        return false;
      }
      say(refused(linkError));
      // The refusals that say what the hub now holds: read it, so the account shows.
      if (
        linkError instanceof ApiError &&
        (linkError.isProblem(ProblemType.participantHasAccount) ||
          linkError.isProblem(ProblemType.codeInUse) ||
          linkError.isProblem(ProblemType.alreadyLinked))
      ) {
        await reread();
      }
      return false;
    }
  };

  const linkExisting = async () => {
    setBusy(true);
    say(null);
    await link(
      (linked) =>
        relation === 'assistant'
          ? `Linked ${linked} as ${displayName}’s assistant.`
          : `Linked ${linked}. It acts for ${displayName} now.`,
      (error) => ({
        tone: 'refused',
        title: 'Not linked',
        text: linkRefusal(error, address, displayName, contacts)
      }),
      (read) => unansweredNote(read, 'link')
    );
    setBusy(false);
  };

  const createAndLink = async () => {
    setBusy(true);
    say(null);
    inbox.begin();
    const request: ICreateAccount = { email: address, kind: 'advisor', ...inbox.body };
    try {
      await accountsApi.createAccount(request);
    } catch (createError) {
      setBusy(false);
      if (inbox.refused(createError, address)) return;
      if (createError instanceof ApiError && createError.isProblem(ProblemType.idpAddressTaken)) {
        say({ tone: 'refused', title: 'Not created', text: IDP_ADDRESS_TAKEN });
        return;
      }
      if (createError instanceof ApiError && createError.status === 409) {
        setMode('existing');
        say({
          tone: 'refused',
          title: 'Not created',
          text:
            `${address} already has an account. Link it as an existing account — it is ` +
            'chosen below.'
        });
        return;
      }
      say({
        tone: 'refused',
        title: 'Not created',
        text: statusRefusal(
          createError,
          { 403: CREATE_ACCOUNT_FORBIDDEN },
          'Could not create this account'
        )
      });
      return;
    }

    const mailedTo = request.deliverTo
      ? `${request.deliverTo} (the test inbox)`
      : 'that address';
    const created = `${address} was created and its invitation to sign in queued`;
    const linked = await link(
      (seat) =>
        `Created ${seat} and linked it to ${displayName}. It signs in once its owner redeems ` +
        `the invitation sent to ${mailedTo}.`,
      (error) => ({
        tone: 'refused',
        title: 'Created, not linked',
        text:
          // Another account got there first, and the re-read takes this panel away: there is
          // nothing below to choose, and the seat is left acting for nobody.
          error instanceof ApiError && error.isProblem(ProblemType.participantHasAccount)
            ? `${created}, but another account now acts for ${displayName} — the new seat ` +
              'acts for nobody.'
            : `${created}, but not linked: ` +
              `${sentence(linkRefusal(error, address, displayName, contacts))} Link it as an existing ` +
              'account — it is chosen below.'
      }),
      (read) => {
        const note = unansweredNote(read, 'link');
        return { ...note, text: `${created}. ${note.text}` };
      }
    );
    if (!linked) setMode('existing');
    setBusy(false);
  };

  return (
    <div className="grid gap-4 rounded-md border border-border p-4 text-sm">
      {relations.includes('assistant') && (
        <fieldset className="grid gap-2">
          <legend className="mb-1 font-medium text-[var(--color-royal-blue)]">Link as</legend>
          {relations.map((candidate) => (
            <label key={candidate} className="flex items-start gap-2">
              <input
                type="radio"
                name={`${modeName}-relation`}
                className="mt-1 accent-[var(--color-sapphire)]"
                checked={relation === candidate}
                disabled={busy || off}
                onChange={() => setRelation(candidate)}
              />
              <span>{LINK_RELATION[candidate]}</span>
            </label>
          ))}
        </fieldset>
      )}
      <fieldset className="grid gap-2">
        <legend className="mb-1 font-medium text-[var(--color-royal-blue)]">
          Link an account
        </legend>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={modeName}
            className="mt-1 accent-[var(--color-sapphire)]"
            checked={mode === 'existing'}
            disabled={busy || off}
            onChange={() => setMode('existing')}
          />
          <span>
            An account that exists
            <span className="block text-muted-foreground">
              The same person under another login: link the account that holds their address.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={modeName}
            className="mt-1 accent-[var(--color-sapphire)]"
            checked={mode === 'new'}
            disabled={busy || off}
            onChange={() => setMode('new')}
          />
          <span>
            A new account
            <span className="block text-muted-foreground">
              A different person, or a shared mailbox: create an advisor seat at another address —
              its owner is invited to sign in — and link that.
            </span>
          </span>
        </label>
      </fieldset>

      <form
        noValidate
        className="grid gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void (mode === 'existing' ? linkExisting() : createAndLink());
        }}
      >
        <label htmlFor={`${modeName}-address`} className="font-medium">
          {LINK_MODE[mode].address}
        </label>
        <div className="flex flex-wrap items-start gap-3">
          <Input
            id={`${modeName}-address`}
            // The sign-in field's attributes, for its reason: an address's shape is survey-api's
            // to judge, and a migrated login can be a bare username.
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            value={email}
            disabled={off}
            aria-describedby={notPermitted}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@firm.com"
            className="max-w-xs"
          />
          <Button
            type="submit"
            disabled={busy || off || address === '' || (mode === 'new' && !inbox.settled)}
            aria-describedby={notPermitted}
          >
            {busy ? LINK_MODE[mode].busy : LINK_MODE[mode].idle}
          </Button>
        </div>
        {mode === 'new' && (
          <TestInboxField
            inbox={inbox}
            id={`${modeName}-deliver-to`}
            intended={email}
            disabled={busy}
          />
        )}
      </form>
    </div>
  );
}

/** A staff link's relation in the words the panel offers it by (SR-487 ruling 19). */
const LINK_RELATION: Record<LinkRelation, string> = {
  self: 'Their own account',
  assistant: 'An assistant'
};

/** What each mode calls its address and its button. */
const LINK_MODE: Record<LinkMode, { address: string; idle: string; busy: string }> = {
  existing: { address: 'Account address', idle: 'Link account', busy: 'Linking…' },
  new: { address: 'New account address', idle: 'Create and link', busy: 'Creating…' }
};
